import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../firebase';
import { collection, getDocs } from 'firebase/firestore';
import './QRList.css';

const MIN_DURATION_MINUTES = 1;

function parseUsedTimestamp(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value.toDate === 'function') return value.toDate();

  if (typeof value === 'string') {
    const trimmed = value.trim();
    const localMatch = trimmed.match(
      /^(\d{1,2})\/(\d{1,2})\/(\d{4}),?\s+(\d{1,2}):(\d{2})(?::(\d{2}))?/
    );

    if (localMatch) {
      const [, day, month, year, hour, minute, second = '0'] = localMatch;
      return new Date(
        Number(year),
        Number(month) - 1,
        Number(day),
        Number(hour),
        Number(minute),
        Number(second)
      );
    }

    const parsed = new Date(trimmed);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  return null;
}

function formatShortTime(date) {
  if (!date) return 'Sin hora';
  return new Intl.DateTimeFormat('es-AR', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function formatHourLabel(date) {
  if (!date) return 'Sin hora';
  return new Intl.DateTimeFormat('es-AR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function formatDuration(minutes) {
  if (minutes < 60) return `${Math.max(MIN_DURATION_MINUTES, Math.round(minutes))} min`;
  return `${(minutes / 60).toLocaleString('es-AR', { maximumFractionDigits: 1 })} h`;
}

function formatRate(value) {
  return value.toLocaleString('es-AR', {
    maximumFractionDigits: value >= 10 ? 0 : 1,
  });
}

const QRList = () => {
  const [tickets, setTickets] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('used');
  const [modelFilter, setModelFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const fetchTickets = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const ticketsSnapshot = await getDocs(collection(db, 'FreeTicket'));
      const fetchedTickets = ticketsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      setTickets(fetchedTickets);
    } catch (error) {
      console.error('Error fetching tickets:', error);
      setError('Error al cargar los tickets. Por favor, intenta nuevamente.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const formatDate = (value) => {
    if (!value) return 'Sin registro';
    try {
      if (typeof value === 'string') return value;
      if (typeof value.toDate === 'function') {
        return value.toDate().toLocaleString('es-AR');
      }
      if (value instanceof Date) {
        return value.toLocaleString('es-AR');
      }
      return String(value);
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Sin registro';
    }
  };

  const normalizeText = (value) => String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

  const ticketItems = useMemo(() => {
    return tickets.flatMap(ticket => {
      const items = Array.isArray(ticket.cartItems) ? ticket.cartItems : [];
      return items.map((item, index) => ({
        ...item,
        parentTicketId: ticket.id,
        rowId: item.qrValue || `${ticket.id}-${index}`,
        orderNumber: item.orderNumber || ticket.orderNumber || ticket.id,
      }));
    });
  }, [tickets]);

  const uniqueModels = useMemo(() => {
    const models = new Set();
    ticketItems.forEach(item => {
      if (item.model) models.add(item.model);
    });
    return Array.from(models).sort((a, b) => a.localeCompare(b, 'es'));
  }, [ticketItems]);

  const summary = useMemo(() => {
    const used = ticketItems.filter(item => item.isUsed).length;
    const unused = ticketItems.length - used;
    return {
      total: ticketItems.length,
      used,
      unused,
      events: uniqueModels.length,
    };
  }, [ticketItems, uniqueModels]);

  const eventPace = useMemo(() => {
    const groupedByEvent = new Map();

    ticketItems.forEach(item => {
      if (!item.isUsed) return;
      if (modelFilter !== 'all' && item.model !== modelFilter) return;

      const usedAt = parseUsedTimestamp(item.usedTimestamp);
      if (!usedAt) return;

      const eventName = item.model || 'Ticket free';
      const current = groupedByEvent.get(eventName) || [];
      current.push({ ...item, usedAt });
      groupedByEvent.set(eventName, current);
    });

    const rows = Array.from(groupedByEvent.entries()).map(([eventName, items]) => {
      const sortedItems = [...items].sort((a, b) => a.usedAt.getTime() - b.usedAt.getTime());
      const firstAt = sortedItems[0].usedAt;
      const lastAt = sortedItems[sortedItems.length - 1].usedAt;
      const rawDurationMinutes = (lastAt.getTime() - firstAt.getTime()) / 60000;
      const durationMinutes = Math.max(MIN_DURATION_MINUTES, rawDurationMinutes);
      const ratePerHour = sortedItems.length / (durationMinutes / 60);

      return {
        eventName,
        count: sortedItems.length,
        firstAt,
        lastAt,
        durationMinutes,
        ratePerHour,
      };
    });

    const maxRate = rows.reduce((max, row) => Math.max(max, row.ratePerHour), 0);

    return rows
      .map(row => ({
        ...row,
        barPercent: maxRate ? Math.max(8, (row.ratePerHour / maxRate) * 100) : 0,
        rateLabel: formatRate(row.ratePerHour),
      }))
      .sort((a, b) => b.ratePerHour - a.ratePerHour);
  }, [modelFilter, ticketItems]);

  const hourlyGrowth = useMemo(() => {
    const buckets = new Map();

    ticketItems.forEach(item => {
      if (!item.isUsed) return;
      if (modelFilter !== 'all' && item.model !== modelFilter) return;

      const usedAt = parseUsedTimestamp(item.usedTimestamp);
      if (!usedAt) return;

      const bucketDate = new Date(usedAt);
      bucketDate.setMinutes(0, 0, 0);
      const bucketKey = bucketDate.getTime();
      const current = buckets.get(bucketKey) || { date: bucketDate, count: 0 };
      buckets.set(bucketKey, { ...current, count: current.count + 1 });
    });

    const sortedBuckets = Array.from(buckets.values()).sort((a, b) => a.date - b.date);
    let cumulative = 0;
    const points = sortedBuckets.map(bucket => {
      cumulative += bucket.count;
      return {
        ...bucket,
        cumulative,
        label: formatHourLabel(bucket.date),
      };
    });

    const chartWidth = 320;
    const chartHeight = 120;
    const paddingX = 14;
    const paddingY = 14;
    const plotWidth = chartWidth - paddingX * 2;
    const plotHeight = chartHeight - paddingY * 2;
    const maxCumulative = Math.max(...points.map(point => point.cumulative), 0);
    const denominator = Math.max(points.length - 1, 1);

    const plottedPoints = points.map((point, index) => {
      const x = points.length === 1
        ? chartWidth / 2
        : paddingX + (index / denominator) * plotWidth;
      const y = chartHeight - paddingY - (point.cumulative / Math.max(maxCumulative, 1)) * plotHeight;

      return {
        ...point,
        x,
        y,
      };
    });

    const linePoints = plottedPoints.map(point => `${point.x},${point.y}`).join(' ');
    const areaPoints = plottedPoints.length
      ? `${paddingX},${chartHeight - paddingY} ${linePoints} ${chartWidth - paddingX},${chartHeight - paddingY}`
      : '';

    return {
      points: plottedPoints,
      linePoints,
      areaPoints,
      maxCumulative,
      total: cumulative,
      firstLabel: plottedPoints[0]?.label || '',
      lastLabel: plottedPoints[plottedPoints.length - 1]?.label || '',
    };
  }, [modelFilter, ticketItems]);

  const filteredTickets = useMemo(() => {
    const normalizedSearch = normalizeText(searchTerm);

    return ticketItems
      .filter(item => {
        const usageMatch = 
          filter === 'all' ? true :
          filter === 'used' ? item.isUsed :
          !item.isUsed;
      
        const modelMatch = 
          modelFilter === 'all' ? true :
          item.model === modelFilter;

        const searchMatch = !normalizedSearch || [
          item.nombre,
          item.model,
          item.orderNumber,
          item.qrValue,
        ].some(value => normalizeText(value).includes(normalizedSearch));

        return usageMatch && modelMatch && searchMatch;
      })
      .sort((a, b) => {
        if (a.isUsed !== b.isUsed) return a.isUsed ? -1 : 1;
        const dateA = a.usedTimestamp || '';
        const dateB = b.usedTimestamp || '';
        return String(dateB).localeCompare(String(dateA));
      });
  }, [filter, modelFilter, searchTerm, ticketItems]);

  const hasActiveFilters = filter !== 'used' || modelFilter !== 'all' || searchTerm.trim() !== '';

  const clearFilters = () => {
    setFilter('used');
    setModelFilter('all');
    setSearchTerm('');
  };

  return (
    <div className="qr-list-page">
      <header className="qr-list-hero">
        <div className="qr-list-hero__content">
          <span className="qr-list-eyebrow">QR y entradas free</span>
          <h1>Ingresos registrados</h1>
          <p>
            Controlá cortesías generadas, entradas usadas y pendientes desde una vista rápida para operación de puerta.
          </p>
        </div>
        <div className="qr-list-hero__actions">
          <button
            type="button"
            className="qr-list-button qr-list-button--secondary"
            onClick={fetchTickets}
            disabled={isLoading}
          >
            {isLoading ? 'Actualizando...' : 'Actualizar'}
          </button>
          <Link className="qr-list-button qr-list-button--primary" to="/admin">
            Volver al panel
          </Link>
        </div>
      </header>

      <section className="qr-list-stats" aria-label="Resumen de entradas free">
        <article className="qr-list-stat">
          <span>Total</span>
          <strong>{summary.total}</strong>
          <small>QR generados</small>
        </article>
        <article className="qr-list-stat qr-list-stat--used">
          <span>Usados</span>
          <strong>{summary.used}</strong>
          <small>Ingresos marcados</small>
        </article>
        <article className="qr-list-stat qr-list-stat--unused">
          <span>Pendientes</span>
          <strong>{summary.unused}</strong>
          <small>Sin escanear</small>
        </article>
        <article className="qr-list-stat">
          <span>Eventos</span>
          <strong>{summary.events}</strong>
          <small>Modelos detectados</small>
        </article>
      </section>

      <section className="qr-list-panel">
        <div className="qr-list-panel__header">
          <div>
            <h2>Listado de tickets</h2>
            <p>
              Mostrando {filteredTickets.length} de {summary.total} entradas.
            </p>
          </div>
          {hasActiveFilters && (
            <button type="button" className="qr-list-clear" onClick={clearFilters}>
              Limpiar filtros
            </button>
          )}
        </div>

        <div className="qr-list-filters">
          <label className="qr-list-filter">
            Buscar
            <input
              type="search"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Nombre, evento, orden o QR"
            />
          </label>

          <label className="qr-list-filter">
            Estado
            <select
              value={filter}
              onChange={e => setFilter(e.target.value)}
            >
              <option value="all">Todos</option>
              <option value="used">Usados</option>
              <option value="unused">No usados</option>
            </select>
          </label>

          <label className="qr-list-filter">
            Evento
            <select
              value={modelFilter}
              onChange={e => setModelFilter(e.target.value)}
            >
              <option value="all">Todos</option>
              {uniqueModels.map(model => (
                <option key={model} value={model}>{model}</option>
              ))}
            </select>
          </label>
        </div>

        <section className="qr-list-pace" aria-label="Temporalidad de ingresos por evento">
          <div className="qr-list-pace__header">
            <div>
              <span className="qr-list-pace__eyebrow">Temporalidad</span>
              <h2>
                {modelFilter === 'all'
                  ? 'Ritmo de ingresos por evento'
                  : `Ritmo de ingresos: ${modelFilter}`}
              </h2>
              <p>
                Calculado con el primer y último escaneo usado del evento seleccionado.
              </p>
            </div>
            {eventPace[0] && (
              <div className="qr-list-pace__highlight">
                <strong>{eventPace[0].rateLabel}</strong>
                <span>{modelFilter === 'all' ? 'entradas/hora mayor ritmo' : 'entradas/hora'}</span>
              </div>
            )}
          </div>

          {eventPace.length === 0 ? (
            <div className="qr-list-pace__empty">
              Aún no hay ingresos usados con horario suficiente para calcular temporalidad en esta selección.
            </div>
          ) : (
            <div className="qr-list-pace__chart">
              {eventPace.map(event => (
                <article className="qr-list-pace__row" key={event.eventName}>
                  <div className="qr-list-pace__meta">
                    <strong>{event.eventName}</strong>
                    <span>
                      {event.count} ingresos · {formatDuration(event.durationMinutes)} · {formatShortTime(event.firstAt)} a {formatShortTime(event.lastAt)}
                    </span>
                  </div>
                  <div className="qr-list-pace__bar-track" aria-hidden="true">
                    <span style={{ width: `${event.barPercent}%` }}></span>
                  </div>
                  <div className="qr-list-pace__rate">
                    <strong>{event.rateLabel}</strong>
                    <span>entradas/h</span>
                  </div>
                </article>
              ))}
            </div>
          )}

          <div className="qr-list-line-chart">
            <div className="qr-list-line-chart__header">
              <div>
                <h3>Crecimiento acumulado por hora</h3>
                <p>Entradas usadas acumuladas en cada tramo horario.</p>
              </div>
              <strong>{hourlyGrowth.total}</strong>
            </div>

            {hourlyGrowth.points.length === 0 ? (
              <div className="qr-list-line-chart__empty">
                No hay horarios usados para dibujar la línea.
              </div>
            ) : (
              <>
                <svg
                  className="qr-list-line-chart__svg"
                  viewBox="0 0 320 120"
                  role="img"
                  aria-label="Crecimiento acumulado de entradas por hora"
                  preserveAspectRatio="none"
                >
                  <line x1="14" y1="106" x2="306" y2="106" className="qr-list-line-chart__axis" />
                  <line x1="14" y1="14" x2="14" y2="106" className="qr-list-line-chart__axis" />
                  {hourlyGrowth.areaPoints && (
                    <polygon points={hourlyGrowth.areaPoints} className="qr-list-line-chart__area" />
                  )}
                  {hourlyGrowth.linePoints && (
                    <polyline points={hourlyGrowth.linePoints} className="qr-list-line-chart__line" />
                  )}
                  {hourlyGrowth.points.map(point => (
                    <circle
                      key={point.date.getTime()}
                      cx={point.x}
                      cy={point.y}
                      r="3.8"
                      className="qr-list-line-chart__dot"
                    >
                      <title>{`${point.label}: ${point.cumulative} acumuladas (${point.count} en la hora)`}</title>
                    </circle>
                  ))}
                </svg>
                <div className="qr-list-line-chart__labels">
                  <span>{hourlyGrowth.firstLabel}</span>
                  <span>{hourlyGrowth.maxCumulative} acumuladas</span>
                  <span>{hourlyGrowth.lastLabel}</span>
                </div>
              </>
            )}
          </div>
        </section>

        {isLoading ? (
          <div className="qr-list-state">
            <span className="qr-list-loader" aria-hidden="true"></span>
            <p>Cargando tickets...</p>
          </div>
        ) : error ? (
          <div className="qr-list-state qr-list-state--error">
            <strong>No se pudo cargar la lista</strong>
            <p>{error}</p>
          </div>
        ) : tickets.length === 0 ? (
          <div className="qr-list-state">
            <strong>No hay tickets generados</strong>
            <p>Cuando se creen entradas free, van a aparecer en este panel.</p>
          </div>
        ) : filteredTickets.length === 0 ? (
          <div className="qr-list-state">
            <strong>Sin resultados</strong>
            <p>No encontramos entradas que coincidan con los filtros seleccionados.</p>
          </div>
        ) : (
          <div className="qr-list-table-wrap">
            <table className="qr-list-table">
              <thead>
                <tr>
                  <th>Invitado</th>
                  <th>Evento</th>
                  <th>Estado</th>
                  <th>Hora de escaneo</th>
                  <th>Orden</th>
                </tr>
              </thead>
              <tbody>
                {filteredTickets.map(item => (
                  <tr key={item.rowId}>
                    <td data-label="Invitado">
                      <span className="qr-list-guest">{item.nombre || 'Sin nombre'}</span>
                    </td>
                    <td data-label="Evento">{item.model || 'Ticket free'}</td>
                    <td data-label="Estado">
                      <span className={`qr-list-status ${item.isUsed ? 'is-used' : 'is-unused'}`}>
                        {item.isUsed ? 'Usado' : 'Pendiente'}
                      </span>
                    </td>
                    <td data-label="Hora de escaneo">
                      {item.isUsed ? formatDate(item.usedTimestamp) : 'Sin uso'}
                    </td>
                    <td data-label="Orden">{item.orderNumber || item.parentTicketId}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default QRList;